import React, {
  FunctionComponent,
  useState,
  ChangeEvent,
  useEffect,
  useContext,
  SyntheticEvent,
} from "react";
import stickybits from "stickybits";
import {
  AppPage,
  FocusableHeader,
  CCXPDropdown,
  LoadingMask,
} from "../../components";
import { Link } from "react-router-dom";
import routes from "../../app/routes";
import {
  TextField,
  Button,
  Choice,
  HelpDrawer,
  ChoiceList,
} from "@cmsgov/ds-medicare-gov";
import * as H from "history";
import { getPharmacies } from "../../api";
import { Feature } from "geojson";
import PharmacySelectionMap from "../../components/PharmacySelectionMap";
import { AppStore } from "../../app/store";
import PharmacySelectionFooter from "./PharmacySelectionFooter";
import {
  ActionType,
  PharmacyType,
  PharmacyGeoPoint,
  PharmacyGeoPoints,
  PharmaciesRequest,
} from "../../@types";
import mapIcon from "./../../assets/map.svg";
import filterIcon from "./../../assets/filter.svg";
import { Close } from "@cmsgov/ds-medicare-gov";
import {
  useTranslate,
  IntlContext,
  CONSTANTS,
  handleFocus,
  sendTealiumEvent,
} from "../../helpers";
import PharmacyPagination from "./PharmacyPagination";
import { useLocation } from "react-router";

export interface HasNextRoute {
  nextRoute?: H.LocationDescriptorObject;
}

const SelectPharmaciesPage: FunctionComponent = () => {
  const {
    dispatch,
    state: { zipcode, pharmacies, pharmacyType, pharmacyAddress },
  } = useContext(AppStore.AppContext);
  const DEFAULT_SEARCH_DISTANCE = 10;
  const { state: locationState } = useLocation();
  const [pharmacyNameSearch, setPharmacyNameSearch] = useState("");
  const [previousNameSearch, setPreviousNameSearch] = useState("");
  const [addressSearch, setAddressSearch] = useState(pharmacyAddress || "");
  const [pharmacyResults, setPharmacyResults] = useState<
    | {
        sourceAddress: PharmacyGeoPoint;
        pharmacies: PharmacyGeoPoints;
        geoError: string;
      }
    | undefined
  >();
  const [page, setPage] = useState(0);
  const [searchDistance, setSearchDistance] = useState(DEFAULT_SEARCH_DISTANCE);
  const [loading, setLoading] = useState(true);
  const [totalPharmacies, setTotalPharmacies] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [showMap, setShowMap] = useState(false);
  const [showMobileFilter, setShowMobileFilter] = useState(false);
  const [mobileSearchDistance, setMobileSearchDistance] = useState<
    number | undefined
  >();
  const t = useTranslate();
  const fm = useContext(IntlContext).formatMessage;
  const [footerMessage, setFooterMessage] = useState("");
  const [nextRoute, setNextRoute] = useState<typeof locationState>();

  useEffect(() => {
    if (locationState && "nextRoute" in (locationState as HasNextRoute)) {
      setNextRoute((locationState as HasNextRoute).nextRoute);
    }
  }, [locationState]);

  const mailOrder = [PharmacyType.MAIL, PharmacyType.MAIL_AND_RETAIL].includes(
    pharmacyType
  );

  const showMapBasedOnResize = (): void => {
    const largeWidthMatch = window.matchMedia("(min-width: 1024px)");

    if (largeWidthMatch.matches) {
      setShowMap(true);
    }
  };

  useEffect(() => {
    showMapBasedOnResize();

    window.addEventListener("resize", showMapBasedOnResize);

    return (): void =>
      window.removeEventListener("resize", showMapBasedOnResize);
  }, []);

  useEffect(() => {
    if (pharmacyResults && showMap) {
      stickybits(".SelectPharmaciesPage__pharmaciesMap");
    }
  }, [pharmacyResults, showMap]);

  const buildAddress = (address: Feature | undefined): string => {
    if (address) {
      const { properties } = address;
      const { street, city, state, zipcode } = properties as {
        street: string;
        city: string;
        state: string;
        zipcode: string;
      };

      return [street, city, state, zipcode]
        .map(s => s.trim())
        .filter(s => !!s)
        .join(", ");
    } else {
      return "";
    }
  };

  const loadPharmacies = async (
    params: {
      radius_miles?: number;
      pageNumber?: number;
      calculateAverageDistance?: boolean;
    } = {}
  ): Promise<void> => {
    setLoading(true);
    if (zipcode) {
      window.scrollTo(0, 0);

      try {
        const trimmedAddress = addressSearch.trim();
        const trimmedPharmName = pharmacyNameSearch.trim();
        const addressSearchIsZip = trimmedAddress.match(/^\d{5}$/gm);
        const pageNum =
          params.pageNumber !== undefined ? params.pageNumber : page;

        const pharmaciesRequest: PharmaciesRequest = {
          zipcode: addressSearchIsZip ? trimmedAddress : zipcode,
          page: pageNum,
          count: CONSTANTS.pharmacySelection.pageCount,
          name: trimmedPharmName,
          address: addressSearchIsZip ? "" : trimmedAddress,
          radius_miles: params.radius_miles || searchDistance,
        };

        const response = await getPharmacies(pharmaciesRequest);

        const {
          pharmacies,
          total_results,
          source_address,
          geocode_error,
        } = response;
        setPharmacyResults({
          pharmacies,
          sourceAddress: source_address,
          geoError: geocode_error,
        });
        setTotalPharmacies(total_results);
        setTotalPages(
          Math.ceil(total_results / CONSTANTS.pharmacySelection.pageCount)
        );
        const builtAddress = buildAddress(source_address);
        setAddressSearch(builtAddress);
        dispatch({
          type: ActionType.UPDATE_PHARMACY_ADDRESS,
          payload: builtAddress,
        });
        setPreviousNameSearch(pharmacyNameSearch);
        setPage(pageNum);

        setLoading(false);

        if (pharmacies.features.length === 0) {
          sendTealiumEvent(
            "Find Plans - Add Pharmacy",
            `Error: ${t("pharm_selection.no_pharmacies")}`
          );
        }

        if (params.calculateAverageDistance) {
          const averageDistance = Math.ceil(
            pharmacies.features.reduce(
              (prev, curr) => prev + curr.properties?.distance_miles,
              0
            ) / pharmacies.features.length
          );

          let roundedDistance;

          if (averageDistance <= 1) {
            roundedDistance = 1;
          } else if (averageDistance <= 5) {
            roundedDistance = 5;
          } else if (averageDistance <= 10) {
            roundedDistance = 10;
          } else {
            roundedDistance = 25;
          }

          setSearchDistance(roundedDistance);

          loadPharmacies({ pageNumber: 0, radius_miles: roundedDistance });
          sendTealiumEvent(
            "Find Plans - Add Pharmacy",
            `Results UI: Filter by: ${roundedDistance} miles - Default`
          );
        }
      } catch (e) {
        setLoading(false);
      }
    }
  };

  enum ToggledFrom {
    "toggledFromList",
    "toggledFromTray",
  }

  const togglePharmacy = (toggledFrom = ToggledFrom.toggledFromList) => (
    npi: string | number,
    pharmacyNumber = 0
  ): void => {
    const castedNpi = Number(npi).toString();

    if (pharmacyResults) {
      const npis = pharmacies.map(p => p.npi);
      const pharmacy = pharmacyResults.pharmacies.features.find(
        pgp => pgp.properties.npi === npi
      );
      const pharmacyName = pharmacy?.properties?.name || "";

      if (!npis.includes(castedNpi)) {
        if (
          CONSTANTS.pharmacySelection.max === pharmacies.length ||
          (pharmacies.length === CONSTANTS.pharmacySelection.max - 1 &&
            mailOrder)
        ) {
          setFooterMessage(
            fm(
              {
                id: "pharmacy_selection_page.max_selection_warning",
              },
              { count: CONSTANTS.pharmacySelection.max }
            )
          );

          return;
        }

        const pharmacy = pharmacyResults.pharmacies.features.find(
          feature => feature.properties?.npi === npi
        );

        dispatch({
          type: ActionType.ADD_PHARMACY,
          payload: pharmacy?.properties,
        });
        sendTealiumEvent(
          "Find Plans - Add Pharmacy",
          `Retail|${pharmacyNumber}|${pharmacyName}|Added`
        );
      } else {
        dispatch({ type: ActionType.DELETE_PHARMACY, payload: npi });

        if (toggledFrom === ToggledFrom.toggledFromTray) {
          sendTealiumEvent(
            "Find Plans - Add Pharmacy",
            `Remove Pharmacy from Top 5`
          );
        }
        sendTealiumEvent(
          "Find Plans - Add Pharmacy",
          `Retail|${pharmacyNumber}|${pharmacyName}|Removed`
        );
      }
    }
  };

  const distanceOptions = [
    {
      label: `${t("pharm_selection.distance")}: 1 ${t("pharm_selection.mile")}`,
      value: 1,
    },
    {
      label: `${t("pharm_selection.distance")}: ${fm(
        { id: "pharm_selection.miles" },
        { numMiles: 5 }
      )}`,
      value: 5,
    },
    {
      label: `${t("pharm_selection.distance")}: ${fm(
        { id: "pharm_selection.miles" },
        { numMiles: 10 }
      )}`,
      value: 10,
    },
    {
      label: `${t("pharm_selection.distance")}: ${fm(
        { id: "pharm_selection.miles" },
        { numMiles: 25 }
      )}`,
      value: 25,
    },
  ];

  const handleRemoveMailOrder = (): void => {
    dispatch({
      type: ActionType.UPDATE_PHARMACY_TYPE,
      payload: PharmacyType.RETAIL,
    });
  };

  useEffect(() => {
    loadPharmacies({
      pageNumber: 0,
      radius_miles: DEFAULT_SEARCH_DISTANCE,
      calculateAverageDistance: true,
    });
  }, [zipcode]);

  useEffect(() => {
    setMobileSearchDistance(showMobileFilter ? searchDistance : undefined);
  }, [showMobileFilter]);

  useEffect(() => {
    if (footerMessage !== "") {
      setFooterMessage("");
    }
  }, [footerMessage]);

  const pharmacyAnchorTarget = "pharmacy-selection-footer-sticky-tray-heading";

  return (
    <>
      <AppPage>
        <>
          <Link
            to={routes.managePrescriptions}
            onClick={(): void => {
              sendTealiumEvent(
                "Find Plans - Add Pharmacy",
                "Back to drug selection"
              );
            }}
            className="SelectPharmaciesPage__backButton"
          >
            <span className="fa fa-chevron-left ds-u-margin-right--1" />
            {t("pharm_selection.back_to_drug_selection")}
          </Link>
          <FocusableHeader
            className="ds-u-margin-top--3 ds-u-margin-bottom--1"
            text={t("pharm_selection.choose_5")}
          />
          <p className="SelectPharmaciesPage__desc">
            {t("pharm_selection.drug_costs")}
          </p>

          <form
            onSubmit={(e): void => {
              e.preventDefault();

              if (zipcode) {
                loadPharmacies({
                  pageNumber: 0,
                  radius_miles: DEFAULT_SEARCH_DISTANCE,
                  calculateAverageDistance: true,
                });
              }
            }}
            className="ds-u-lg-display--flex ds-u-margin-top--1 ds-u-lg-margin-top--3"
          >
            <TextField
              className="SelectPharmaciesPage__zipInput e2e-pharm-zip"
              name="pharm-address"
              value={addressSearch}
              label={t("pharm_selection.pharm_address_input")}
              onFocus={(): void => {
                sendTealiumEvent(
                  "Find Plans - Add Pharmacy",
                  "UI Form: Enter your zip code or street address"
                );
              }}
              onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                setAddressSearch(event.target.value);
              }}
            />
            <TextField
              label={t("pharm_selection.pharm_name_input")}
              name="pharm-name"
              value={pharmacyNameSearch}
              className="SelectPharmaciesPage__pharmNameInput"
              onFocus={(): void => {
                sendTealiumEvent(
                  "Find Plans - Add Pharmacy",
                  "UI Form: Name of pharmacy"
                );
              }}
              onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                setPharmacyNameSearch(event.target.value);
              }}
            />
            <Button
              type="submit"
              variation="primary"
              className="SelectPharmaciesPage__searchButton"
              onClick={(): void => {
                sendTealiumEvent(
                  "Find Plans - Add Pharmacy",
                  "CTA: Find Pharmacy"
                );
              }}
            >
              {t("pharm_selection.find_pharmacy")}
            </Button>
          </form>
          <div className="SelectPharmaciesPage__resultsContainer">
            <div className="SelectPharmaciesPage__distanceFilter">
              <div className="ds-u-display--flex ds-u-lg-display--none ds-u-justify-content--center mct-u-full-width">
                <Button
                  className="ds-c-button--primary-alt-outline ds-u-margin-right--2 ds-u-display--flex ds-u-align-items--center"
                  onClick={(): void => setShowMobileFilter(!showMobileFilter)}
                >
                  <img src={filterIcon} alt="" />{" "}
                  <span className="ds-u-margin-left--1">
                    {t("pharm_selection.filter")}
                  </span>
                </Button>
                <Button
                  className="ds-c-button--primary-alt ds-u-display--flex ds-u-align-items--center"
                  onClick={(): void => {
                    setShowMap(!showMap);
                    sendTealiumEvent(
                      "Find Plans - Add Pharmacy",
                      `${showMap ? "Unhide" : "Hide"} Map`
                    );
                  }}
                >
                  <img src={mapIcon} alt="" />
                  <span className="ds-u-margin-left--1">
                    {!showMap
                      ? t("pharm_selection.show_map")
                      : t("pharm_selection.hide_map")}
                  </span>
                </Button>
              </div>

              {showMobileFilter && (
                <div className="mct-c-help-drawer">
                  <HelpDrawer
                    title={t("pharm_selection.filters")}
                    onCloseClick={(): void => {
                      setShowMobileFilter(false);
                      sendTealiumEvent(
                        "Find Plans - Add Pharmacy",
                        "Close Filters"
                      );
                    }}
                    closeButtonText={<Close />}
                  >
                    <>
                      <ChoiceList
                        type="radio"
                        name="distance"
                        choices={distanceOptions.map(c => ({
                          ...c,
                          checked: c.value == mobileSearchDistance,
                        }))}
                        onChange={(
                          e: SyntheticEvent<HTMLInputElement>
                        ): void => {
                          setMobileSearchDistance(
                            Number(e.currentTarget.value)
                          );
                        }}
                        label={t("pharm_selection.distance")}
                      />
                      <div className="SelectPharmaciesPage__mobileFilterActions">
                        <Button
                          variation="transparent"
                          className="ds-c-button--primary-alt ds-u-margin-right--2"
                        >
                          {t("pharm_selection.clear_all")}
                        </Button>
                        <Button
                          className="ds-c-button--primary-alt"
                          onClick={(): void => {
                            if (mobileSearchDistance) {
                              setSearchDistance(mobileSearchDistance);
                              loadPharmacies({
                                radius_miles: mobileSearchDistance,
                                pageNumber: 0,
                              });
                            }

                            setShowMobileFilter(false);
                          }}
                        >
                          {t("pharm_selection.apply")}
                        </Button>
                      </div>
                    </>
                  </HelpDrawer>
                </div>
              )}
              <div className="ds-u-display--none ds-u-lg-display--flex ds-u-align-items--center">
                <p className="ds-u-margin-right--2">
                  {t("pharm_selection.filter_by")}:
                </p>
                <CCXPDropdown
                  value={searchDistance}
                  label={t("pharm_selection.distance")}
                  options={distanceOptions}
                  onUpdate={(value: string | number): void => {
                    const dist = Number(value);

                    setSearchDistance(dist);
                    loadPharmacies({
                      radius_miles: dist,
                      pageNumber: 0,
                    });
                    sendTealiumEvent(
                      "Find Plans - Add Pharmacy",
                      `Results UI: Filter by: ${dist} miles`
                    );
                  }}
                />
              </div>
            </div>
            <div className="SelectPharmaciesPage__results">
              <div className="SelectPharmaciesPage__pharmacyOptionsContainer">
                <div
                  role="status"
                  aria-live="polite"
                  className="SelectPharmaciesPage__grayBottomBorder ds-u-padding-y--2 ds-u-padding-left--2 ds-u-md-padding-left--7"
                >
                  {pharmacyResults &&
                  pharmacyResults.pharmacies.features.length > 0
                    ? fm(
                        { id: "pharm_selection.pharmacy_count" },
                        {
                          pharmacyLowerBound:
                            CONSTANTS.pharmacySelection.pageCount * page + 1,
                          pharmacyUpperBound:
                            CONSTANTS.pharmacySelection.pageCount * page +
                            pharmacyResults?.pharmacies.features.length,
                          totalPharmacies,
                          for: previousNameSearch
                            ? fm(
                                { id: "pharm_selection.for" },
                                {
                                  for: previousNameSearch,
                                }
                              )
                            : "",
                          address: fm(
                            { id: "pharm_selection.near_address" },
                            {
                              address: buildAddress(
                                pharmacyResults.sourceAddress
                              ),
                            }
                          ),
                        }
                      ).replace(/\s+/gm, " ")
                    : t("pharm_selection.showing_0_pharmacies")}
                </div>

                <div className="SelectPharmaciesPage__grayBottomBorder ds-u-padding-y--1">
                  <div className="SelectPharmaciesPage__pharmacyCard e2e-mail-order-pharmacy">
                    <div className="ds-u-display--flex">
                      {pharmacyResults &&
                        pharmacyResults.pharmacies.features.length > 0 && (
                          <span className="SelectPharmaciesPage__pharmacyNumber">
                            &nbsp;
                          </span>
                        )}
                      <div className="SelectPharmaciesPage__pharmacyInfo">
                        <h2 className="SelectPharmaciesPage__pharmacyCardHeader">
                          {t("pharm_selection.mail_order_pharm")}
                        </h2>
                        <p className="ds-u-margin--0">
                          {t("pharm_selection.mail_order_pharm_desc")}
                        </p>
                      </div>
                    </div>

                    <div className="SelectPharmaciesPage__addPharmacy">
                      <Choice
                        type="checkbox"
                        name="include-mail-order-pharmacy"
                        value="mail-order-pharmacy"
                        checked={mailOrder}
                        onChange={(): void => {
                          if (mailOrder) {
                            dispatch({
                              type: ActionType.UPDATE_PHARMACY_TYPE,
                              payload: PharmacyType.RETAIL,
                            });
                            sendTealiumEvent(
                              "Find Plans - Add Pharmacy",
                              "Mail Order Pharmacy|Removed"
                            );
                          } else {
                            dispatch({
                              type: ActionType.UPDATE_PHARMACY_TYPE,
                              payload: PharmacyType.MAIL_AND_RETAIL,
                            });
                            sendTealiumEvent(
                              "Find Plans - Add Pharmacy",
                              "Mail Order Pharmacy|Added"
                            );
                          }
                        }}
                      >
                        {mailOrder
                          ? t("pharm_selection.pharmacy_added")
                          : t("pharm_selection.add_pharmacy")}
                      </Choice>
                    </div>

                    <a
                      className="SelectPharmaciesPage__skip-link"
                      href={`#${pharmacyAnchorTarget}`}
                      onClick={(e): void =>
                        handleFocus(e, pharmacyAnchorTarget)
                      }
                    >
                      {t("pharmacy_skip_text")}
                    </a>
                  </div>
                </div>

                {!loading &&
                  pharmacyResults &&
                  !pharmacyResults.pharmacies.features.length && (
                    <div className="ds-u-padding-y--3 ds-u-padding-left--2 ds-u-md-padding-left--7 ds-u-md-padding-right--3">
                      <h2>{t("pharm_selection.no_pharmacies")}</h2>
                      <p>{t("pharm_selection.improve_search")}</p>
                      <ul className="ds-c-list--bare" role="list">
                        <li role="listitem">
                          <p>{t("pharm_selection.try_different_area")}</p>
                        </li>
                        <li role="listitem">
                          <p>{t("pharm_selection.check_spelling")}</p>
                        </li>
                      </ul>
                    </div>
                  )}

                {pharmacyResults && (
                  <ol className="SelectPharmaciesPage__pharmacies" role="list">
                    {pharmacyResults.pharmacies.features.map(
                      (pharmacy, index) => {
                        const { properties } = pharmacy;
                        const {
                          npi,
                          name,
                          phone,
                          city,
                          state,
                          street,
                          unit,
                          zipcode,
                        } = properties;

                        const ucFirst = (str: string): string =>
                          str.substring(0, 1).toUpperCase() +
                          str.substring(1).toLowerCase();

                        const normalizeStringCase = (str: string): string =>
                          str
                            .split(" ")
                            .map(s => ucFirst(s))
                            .join(" ");

                        const formatPhone = (phone: string): string =>
                          `(${phone.substring(0, 3)}) ${phone.substring(
                            3,
                            6
                          )}-${phone.substring(6)}`;

                        const address = `${normalizeStringCase(street)}${
                          unit ? ` ${normalizeStringCase(unit)}` : ""
                        }, ${normalizeStringCase(
                          city
                        )}, ${state} ${zipcode.substring(0, 5)}`;

                        const isSelected = pharmacies
                          .map(p => p.npi)
                          .includes(npi);

                        const pharmacyNumber =
                          CONSTANTS.pharmacySelection.pageCount * page +
                          (index + 1);

                        return (
                          <li
                            className="SelectPharmaciesPage__pharmacyCard"
                            key={npi}
                            id={npi}
                            role="listitem"
                          >
                            <div className="ds-u-display--flex">
                              <span className="SelectPharmaciesPage__pharmacyNumber">
                                {pharmacyNumber}.
                              </span>
                              <div className="SelectPharmaciesPage__pharmacyInfo">
                                <h2 className="SelectPharmaciesPage__pharmacyCardHeader">
                                  {normalizeStringCase(name)}
                                </h2>
                                <p className="ds-u-margin--0">
                                  {address}
                                  <br />
                                  {formatPhone(phone)}
                                </p>
                              </div>
                            </div>
                            <div className="SelectPharmaciesPage__addPharmacy">
                              <Choice
                                type="checkbox"
                                name="pharmacy-1"
                                value={npi}
                                className={`SelectPharmaciesPage__pharmaciesOption e2e-select-pharmacy-${index}`}
                                checked={isSelected}
                                onChange={(): void =>
                                  togglePharmacy()(npi, pharmacyNumber)
                                }
                              >
                                {isSelected
                                  ? t("pharm_selection.pharmacy_added")
                                  : t("pharm_selection.add_pharmacy")}
                              </Choice>
                            </div>

                            <a
                              className="SelectPharmaciesPage__skip-link"
                              href={`#${pharmacyAnchorTarget}`}
                              onClick={(e): void =>
                                handleFocus(e, pharmacyAnchorTarget)
                              }
                            >
                              {t("pharmacy_skip_text")}
                            </a>
                          </li>
                        );
                      }
                    )}
                  </ol>
                )}

                {!loading &&
                  pharmacyResults &&
                  pharmacyResults.pharmacies.features.length > 0 && (
                    <PharmacyPagination
                      currentPage={page}
                      pageCount={totalPages}
                      onNextPage={(): void => {
                        const pagePlusOne = page + 1;
                        setPage(pagePlusOne);
                        loadPharmacies({
                          pageNumber: pagePlusOne,
                        });
                      }}
                      onPrevPage={(): void => {
                        const pageMinusOne = page - 1;
                        setPage(pageMinusOne);
                        loadPharmacies({
                          pageNumber: pageMinusOne,
                        });
                      }}
                      handlePageClick={(pageNum): void => {
                        setPage(pageNum);
                        loadPharmacies({
                          pageNumber: pageNum,
                        });
                      }}
                    />
                  )}
              </div>
              {pharmacyResults && showMap && (
                <div className="SelectPharmaciesPage__pharmaciesMap">
                  <PharmacySelectionMap
                    data={pharmacyResults}
                    selectedPharmacies={pharmacies}
                    page={page}
                  />
                </div>
              )}
            </div>
          </div>
          <LoadingMask loading={loading} delay={500} />
        </>
      </AppPage>
      <PharmacySelectionFooter
        selectionList={pharmacies}
        onRemovePharmacyFromSelection={togglePharmacy(
          ToggledFrom.toggledFromTray
        )}
        hasMailOrder={mailOrder}
        onRemoveMailOrder={handleRemoveMailOrder}
        pageLoading={loading}
        warningMessage={footerMessage}
        nextRoute={nextRoute}
      />
    </>
  );
};

export default SelectPharmaciesPage;
